import { useCallback, useMemo, useState } from 'react';
import type React from 'react';

const hasValue = (value: unknown): boolean => {
    if (Array.isArray(value)) {
        return value.some((item) => hasValue(item));
    }

    if (value === null || value === undefined) {
        return false;
    }

    if (typeof value === 'string') {
        return value.trim().length > 0;
    }

    return true;
};

type FloatingLabelOptions = {
    value: unknown;
    onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
};

/**
 * Keeps outlined labels inside empty fields and floats them only while focused
 * or when the field already has a value. This explicitly neutralizes MUI's
 * default `adornedStart => shrink` behavior so icon fields behave like normal
 * outlined inputs.
 */
export const useFloatingLabel = ({
    value,
    onFocus,
    onBlur,
}: FloatingLabelOptions) => {
    const [focused, setFocused] = useState(false);

    const handleFocus = useCallback<
        React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    >(
        (event) => {
            setFocused(true);
            onFocus?.(event);
        },
        [onFocus],
    );

    const handleBlur = useCallback<
        React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    >(
        (event) => {
            setFocused(false);
            onBlur?.(event);
        },
        [onBlur],
    );

    const shrink = useMemo(() => focused || hasValue(value), [focused, value]);

    return {
        focused,
        shrink,
        onFocus: handleFocus,
        onBlur: handleBlur,
    };
};
